import type {
    TraderProfile,
    TraderActivity,
    TraderPerformanceMetrics,
    TraderFinancials,
    TraderCategoryStats,
    TraderPosition,
    TraderBehaviorSignals,
} from './traderProfileTypes';

function num(v: unknown, fallback = 0): number {
    const n =
        typeof v === 'number'
            ? v
            : typeof v === 'string' && v.trim() !== ''
              ? Number(v)
              : NaN;
    return Number.isFinite(n) ? n : fallback;
}

function numOrNull(v: unknown): number | null {
    if (v === null || v === undefined) return null;
    const n =
        typeof v === 'number'
            ? v
            : typeof v === 'string' && v.trim() !== ''
              ? Number(v)
              : NaN;
    return Number.isFinite(n) ? n : null;
}

function normalizeActivity(a: TraderActivity): TraderActivity {
    return {
        ...a,
        active_days: num(a.active_days),
        total_trades: num(a.total_trades),
        avg_size_usd: num(a.avg_size_usd),
        lifetime_volume_usd: num(a.lifetime_volume_usd),
        unique_markets: num(a.unique_markets),
        closed_count: num(a.closed_count),
        avg_hold_days: num(a.avg_hold_days),
        trade_freq_per_day: num(a.trade_freq_per_day),
        hourly_activity: Array.isArray(a.hourly_activity)
            ? a.hourly_activity.map(v => num(v))
            : [],
        size_distribution: Array.isArray(a.size_distribution)
            ? a.size_distribution.map(s => ({
                  bucket: s.bucket,
                  count: num(s.count),
              }))
            : [],
    };
}

function normalizePerformance(
    p: TraderPerformanceMetrics
): TraderPerformanceMetrics {
    return {
        win_rate: num(p.win_rate),
        total_pnl: num(p.total_pnl),
        realized_pnl: num(p.realized_pnl),
        roi_pct: num(p.roi_pct),
        roe_pct: numOrNull(p.roe_pct),
        profit_factor: num(p.profit_factor),
        daily_sharpe: numOrNull(p.daily_sharpe),
        position_sharpe: numOrNull(p.position_sharpe),
        max_pnl_drawdown_usd: numOrNull(p.max_pnl_drawdown_usd),
        roct: num(p.roct),
        twr_pct: num(p.twr_pct),
    };
}

function normalizeFinancials(f: TraderFinancials): TraderFinancials {
    return {
        balance_usd: numOrNull(f.balance_usd),
        portfolio_value: numOrNull(f.portfolio_value),
        net_deposits: numOrNull(f.net_deposits),
        total_deposits: numOrNull(f.total_deposits),
        total_withdrawals: numOrNull(f.total_withdrawals),
    };
}

function normalizeCategoryStats(c: TraderCategoryStats): TraderCategoryStats {
    return {
        market_count: num(c.market_count),
        closed_count: num(c.closed_count),
        win_count: num(c.win_count),
        win_rate: num(c.win_rate),
        total_pnl: num(c.total_pnl),
        realized_pnl: num(c.realized_pnl),
        volume_usd: num(c.volume_usd),
        profit_factor: num(c.profit_factor),
        position_sharpe: numOrNull(c.position_sharpe),
        avg_hold_days: num(c.avg_hold_days),
        avg_edge: num(c.avg_edge),
        roct: num(c.roct),
        market_liquidity_score: numOrNull(c.market_liquidity_score),
    };
}

function normalizePosition(p: TraderPosition): TraderPosition {
    return {
        ...p,
        size_usd: num(p.size_usd),
        entry_price: num(p.entry_price),
        exit_price: numOrNull(p.exit_price),
        avg_exit_price: numOrNull(p.avg_exit_price),
        current_price: numOrNull(p.current_price),
        current_value: numOrNull(p.current_value),
        cost_basis: numOrNull(p.cost_basis),
        total_buy_quote: numOrNull(p.total_buy_quote),
        total_sell_quote: numOrNull(p.total_sell_quote),
        realized_pnl: num(p.realized_pnl),
        unrealized_pnl: num(p.unrealized_pnl),
        hold_days: numOrNull(p.hold_days),
        pnl: num(p.pnl),
        buy_count: numOrNull(p.buy_count),
        sell_count: numOrNull(p.sell_count),
    };
}

export function normalizePositionRow(p: TraderPosition): TraderPosition {
    return normalizePosition(p);
}

function normalizeBehaviorSignals(
    b: TraderBehaviorSignals
): TraderBehaviorSignals {
    return {
        algo_likelihood: num(b.algo_likelihood),
        repeat_market_rate: num(b.repeat_market_rate),
        off_hours_activity: num(b.off_hours_activity),
        maker_ratio: num(b.maker_ratio),
        avg_entry_prob: num(b.avg_entry_prob),
        avg_edge: num(b.avg_edge),
        entry_prob_buckets: Array.isArray(b.entry_prob_buckets)
            ? b.entry_prob_buckets.map(v => num(v))
            : [],
        capital_utilization: numOrNull(b.capital_utilization),
        market_liquidity_score: numOrNull(b.market_liquidity_score),
    };
}

export function normalizeTraderProfile(raw: TraderProfile): TraderProfile {
    const category_metrics: Record<string, TraderCategoryStats> = {};
    for (const [category, stats] of Object.entries(
        raw.category_metrics ?? {}
    )) {
        category_metrics[category] = normalizeCategoryStats(stats);
    }

    return {
        ...raw,
        activity: normalizeActivity(raw.activity),
        performance: normalizePerformance(raw.performance),
        financials: normalizeFinancials(raw.financials),
        behavior: raw.behavior
            ? normalizeBehaviorSignals(raw.behavior)
            : raw.behavior,
        category_metrics,
    };
}
